import type { ReactNode } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { OrgAvatar } from '@/components/encuentro/objects/org-avatar'
import { PageHeader } from '@/components/encuentro/page/page-header'

/** The school on an org-branded entry point (its login, its invitation). */
export type AuthOrg = { name: string; logoUrl: string | null }

/**
 * Inline link inside the auth pages: ink with an underline (sistema/README.md,
 * Color). The text is 12 to 14 px, so the `after` pseudo-element grows the
 * touch area to 44 px without moving anything (ALU-678).
 */
export const authLinkClass =
  "relative rounded-en-sm after:absolute after:inset-x-0 after:-inset-y-3.5 after:content-[''] text-en-ink underline underline-offset-4 hover:text-en-ink-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"

/**
 * The page every signed-out or not-yet-member screen sits on (ALU-73): sign
 * in, recovery, an invitation, the access gates.
 *
 * - The institution first (sistema/README.md, "La institución primero"): on an
 *   org-branded entry point the school's logo and name head the card, and
 *   Alumnia appears once, as "Powered by Alumnia" at the foot. Without a
 *   school, the Alumnia lockup heads the card and there is no endorsement.
 * - Desktop: a card (`surface`, `shadow-card`, `radius-lg`) on the `canvas`.
 *   Phone: the content straight on `bg`, full width.
 * - It is its own scroll container (`fixed`, like `FramelessMain`), so it
 *   scrolls whatever the route's layout does with `html` and `body`.
 * - `landmark={false}` when a parent already renders `main` (the access gates,
 *   inside `FramelessMain`).
 */
export function AuthShell({
  org,
  locale,
  children,
  footer,
  landmark = true,
  banner,
}: {
  org: AuthOrg | null
  /** The language switcher, top right. */
  locale?: ReactNode
  children: ReactNode
  /** Under the card: the legal line, a way back. */
  footer?: ReactNode
  landmark?: boolean
  /** Above everything (the view-as banner). */
  banner?: ReactNode
}) {
  const Content = landmark ? 'main' : 'div'
  return (
    <div
      data-slot="auth-shell"
      className="fixed inset-0 grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr_auto] overflow-y-auto bg-en-bg font-en-sans text-en-ink pt-safe pb-safe pl-safe pr-safe md:bg-en-canvas"
    >
      <div className="grid grid-cols-[minmax(0,1fr)]">
        {banner}
        <div className="flex min-h-16 items-center justify-end gap-3 px-4 py-3 md:px-8">
          {locale}
        </div>
      </div>
      <Content
        id={landmark ? 'main' : undefined}
        tabIndex={landmark ? -1 : undefined}
        className="grid grid-cols-[minmax(0,1fr)] content-start justify-items-center px-4 pb-10 outline-none md:content-center md:px-8"
      >
        <div
          data-slot="auth-card"
          className="grid w-full max-w-md grid-cols-[minmax(0,1fr)] content-start gap-8 md:rounded-en-lg md:bg-en-surface md:p-10 md:shadow-en-card"
        >
          <AuthIdentity org={org} />
          {children}
        </div>
        {footer && (
          <div className="mt-6 grid w-full max-w-md grid-cols-[minmax(0,1fr)] justify-items-center gap-3 text-center">
            {footer}
          </div>
        )}
      </Content>
      {org ? <PoweredBy /> : <div aria-hidden className="h-6" />}
    </div>
  )
}

/** The school's logo and name, or the Alumnia lockup. Never a heading: the page has its own `h1`. */
function AuthIdentity({ org }: { org: AuthOrg | null }) {
  const t = useTranslations('encuentro.auth.brand')
  if (!org) {
    return (
      <div className="flex justify-center md:justify-start">
        <Image
          src="/brand/encuentro/lockup-horizontal-blue.svg"
          alt={t('alumnia')}
          width={124}
          height={32}
          priority
          unoptimized
          className="h-8 w-auto"
        />
      </div>
    )
  }
  return (
    <div
      data-slot="auth-org"
      className="flex flex-col items-center gap-3 text-center md:flex-row md:text-left"
    >
      <OrgAvatar name={org.name} logoUrl={org.logoUrl} size="lg" shape="square" />
      <p className="min-w-0 text-en-subheading [overflow-wrap:anywhere]">{org.name}</p>
    </div>
  )
}

/** "Powered by Alumnia", once, at the foot of an org-branded page (also the onboarding's). */
export function PoweredBy() {
  const t = useTranslations('encuentro.auth.brand')
  return (
    <p
      data-slot="auth-powered-by"
      className="flex items-center justify-center gap-2 px-4 pt-4 pb-6 text-en-caption text-en-ink-muted"
    >
      <Image
        src="/brand/encuentro/symbol-micro-blue.svg"
        alt=""
        width={14}
        height={14}
        unoptimized
        className="h-3.5 w-auto"
      />
      {t('poweredBy')}
    </p>
  )
}

/**
 * The page title block: `PageHeader`, the one `h1` of the new tree, with its
 * line. The school's name is never the heading (`AuthIdentity` shows it).
 */
export function AuthHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return <PageHeader title={title} subtitle={subtitle} />
}
